import {
  MutableRefObject,
  MouseEvent as ReactMouseEvent,
  RefObject,
  TouchEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';

interface Params {
  carouselRef: RefObject<HTMLDivElement>;
  currentIndex: number;
  lastIndex: number;
  isInfiniteCarousel: boolean;
  isSupportMouseEvent: boolean;
  goToNext: () => void;
  goToPrev: () => void;
  minPxToSwipeItem: number;
  onTransitionCarouselRef: MutableRefObject<boolean>;
  startAutoplay: () => void;
  stopAutoplay: () => void;
  isAutoplay: boolean;
  slideSpeed: number;
}

// 캐러셀에 붙는 이벤트들을 관리한다
export function useCarouselEvents({
  carouselRef,
  currentIndex,
  lastIndex,
  isInfiniteCarousel,
  isSupportMouseEvent,
  goToNext,
  goToPrev,
  minPxToSwipeItem,
  onTransitionCarouselRef,
  startAutoplay,
  stopAutoplay,
  isAutoplay,
  slideSpeed,
}: Params) {
  const clientXOnTouchStart = useRef(0);
  const carouselPositionXOnTouchStart = useRef(0);
  const shouldIgnoreNextTouchEvent = useRef(false);
  const shouldIgnoreNextMouseEvent = useRef(false);

  const indexOnTouchStart = useRef(0);

  const touchStartHandler = useCallback(
    (clientX: number) => {
      stopAutoplay();
      indexOnTouchStart.current = currentIndex;
      clientXOnTouchStart.current = clientX;
      if (carouselRef.current) {
        carouselPositionXOnTouchStart.current = getElementTranslateX(
          carouselRef.current
        );
      }
    },
    [carouselRef, stopAutoplay, currentIndex]
  );

  const onTouchStartCarousel = useCallback(
    (e: TouchEvent) => {
      // Touch이벤트가 Mouse이벤트보다 먼저 발생한다. 혹시 Touch, Mouse 이벤트를 둘 다 발생시키는 환경이 있을 수 있으므로 Touch이벤트가 발생했다면 이후의 마우스이벤트는 동작시키지 않음
      shouldIgnoreNextMouseEvent.current = true;
      // TouchStart 시점에 transition값이 true라면, 슬라이드가 아직 진행중인 상태이므로 그 뒤 touchevent를 모두 처리하지 않는다.
      // 기존에는 preventDefault로 후속 이벤트를 발생시키지 않을 수 있었던걸로 기억하는데, react17 버전부터 이벤트 처리가 변경되어 passive true를 달고 있기 때문에 동작하지 않음
      // Nuka Carousel에서도 같은 이슈를 처리한 적이 있어 참고 링크를 추가한다. (https://github.com/FormidableLabs/nuka-carousel/pull/765)
      if (onTransitionCarouselRef.current) {
        shouldIgnoreNextTouchEvent.current = true;
        return;
      }
      touchStartHandler(e.touches[0].clientX);
    },
    [touchStartHandler, onTransitionCarouselRef]
  );

  const touchMoveHandler = useCallback(
    (currentClientX: number) => {
      if (!carouselRef.current) {
        return;
      }
      const movingPosition = clientXOnTouchStart.current - currentClientX;
      // Infinite가 아닌 경우 슬라이드 끝에 다다르면 더 갈 수 없다
      if (
        !isInfiniteCarousel &&
        currentIndex === lastIndex &&
        movingPosition > 0
      ) {
        return;
      }
      if (indexOnTouchStart.current !== currentIndex) {
        return;
      }
      // Infinite가 아닌 경우 슬라이드 처음에서는 더 이전으로 갈 수 없다
      if (!isInfiniteCarousel && currentIndex === 0 && movingPosition < 0) {
        return;
      }
      carouselRef.current.style.transform = `translateX(${
        carouselPositionXOnTouchStart.current - movingPosition
      }px)`;
    },
    [isInfiniteCarousel, currentIndex, lastIndex, carouselRef]
  );

  const onTouchMoveCarousel = useCallback(
    (e: TouchEvent) => {
      if (shouldIgnoreNextTouchEvent.current) {
        return;
      }
      touchMoveHandler(e.touches[0].clientX);
    },
    [touchMoveHandler]
  );
  const cancelClickEventTimer = useRef(0);
  const cancelEvent = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const clearCancelClickEvent = useCallback(() => {
    if (!carouselRef.current) {
      return;
    }
    carouselRef.current.removeEventListener('click', cancelEvent, {
      capture: true,
    });
    window.clearTimeout(cancelClickEventTimer.current);
  }, [cancelEvent, carouselRef]);

  const touchEndHandler = useCallback(
    (endClientX: number) => {
      if (indexOnTouchStart.current !== currentIndex) {
        return;
      }
      if (
        Math.abs(endClientX - clientXOnTouchStart.current) < minPxToSwipeItem
      ) {
        if (carouselRef.current) {
          carouselRef.current.style.transform = `translateX(${carouselPositionXOnTouchStart.current}px)`;
        }
        return;
      }

      if (carouselRef.current) {
        clearCancelClickEvent();
        // 슬라이드 넘기는 동안 클릭이벤트를 방지합니다.
        carouselRef.current.addEventListener('click', cancelEvent, {
          capture: true,
        });
        cancelClickEventTimer.current = window.setTimeout(() => {
          carouselRef.current?.removeEventListener('click', cancelEvent, {
            capture: true,
          });
        }, slideSpeed);
      }

      if (endClientX > clientXOnTouchStart.current) {
        goToPrev();
      }
      if (endClientX < clientXOnTouchStart.current) {
        goToNext();
      }
    },
    [
      currentIndex,
      minPxToSwipeItem,
      clearCancelClickEvent,
      cancelEvent,
      slideSpeed,
      carouselRef,
      goToPrev,
      goToNext,
    ]
  );

  useEffect(() => {
    () => {
      clearCancelClickEvent();
    };
  }, [clearCancelClickEvent]);

  const onTouchEndCarousel = useCallback(
    (e: TouchEvent) => {
      if (isAutoplay) {
        startAutoplay();
      }
      if (shouldIgnoreNextTouchEvent.current) {
        shouldIgnoreNextTouchEvent.current = false;
        return;
      }
      touchEndHandler(e.changedTouches[0].clientX);
    },
    [touchEndHandler, startAutoplay, isAutoplay]
  );

  const onMouseMoveCarousel = useCallback(
    (e: MouseEvent) => {
      if (shouldIgnoreNextMouseEvent.current) {
        return;
      }
      touchMoveHandler(e.clientX);
    },
    [touchMoveHandler]
  );

  const onMouseUpCarousel = useCallback(
    (e: MouseEvent) => {
      if (isAutoplay) {
        startAutoplay();
      }
      window.removeEventListener('mousemove', onMouseMoveCarousel);
      if (shouldIgnoreNextMouseEvent.current) {
        shouldIgnoreNextMouseEvent.current = false;
        return;
      }
      touchEndHandler(e.clientX);
    },
    [touchEndHandler, onMouseMoveCarousel, isAutoplay, startAutoplay]
  );

  const onMouseDownCarousel = useCallback(
    (e: ReactMouseEvent) => {
      if (onTransitionCarouselRef.current) {
        shouldIgnoreNextMouseEvent.current = true;
      }
      // 터치이벤트가 먼저 발생하거나 하는 등으로 마우스이벤트를 무시하기로 결정 된 경우이다. 다만, 중간에 환경이 바뀔 수 있으므로 마우스를 뗄 때 해당 값을 다시 false로 초기화해준다
      if (shouldIgnoreNextMouseEvent.current) {
        window.addEventListener(
          'mouseup',
          () => (shouldIgnoreNextMouseEvent.current = false),
          {once: true}
        );
        return;
      }
      if (!isSupportMouseEvent) {
        return;
      }
      touchStartHandler(e.clientX);
      // mouse이벤트는 이벤트 발생 element 바깥으로 마우스가 움직이면 이벤트가 발생하지 않으므로 window에 걸어준다
      // ts버전문제로 global MouseEvent 타입을 이상한 곳에서 가져온다 (redux의 디펜던시 react type에서 가져옴. 그래서 타입호환이 되지 않아 우선 ignore걸어놓음)
      //@ts-ignore
      window.addEventListener('mousemove', onMouseMoveCarousel);
      //@ts-ignore
      window.addEventListener('mouseup', onMouseUpCarousel, {once: true});
    },
    [
      isSupportMouseEvent,
      onMouseMoveCarousel,
      onMouseUpCarousel,
      onTransitionCarouselRef,
      touchStartHandler,
    ]
  );
  return {
    onMouseDownCarousel,
    onTouchEndCarousel,
    onTouchMoveCarousel,
    onTouchStartCarousel,
  };
}

function getElementTranslateX(element: HTMLElement) {
  if (!element) {
    return 0;
  }
  const computedStyle = window.getComputedStyle(element);
  const matrix = computedStyle.transform || computedStyle.webkitTransform;
  const matrixType = matrix.includes('3d') ? '3d' : '2d';
  const matrixValues = matrix.match(/matrix.*\((.+)\)/)?.[1].split(', ');
  if (!matrixValues) {
    return 0;
  }
  return Number(matrixType === '3d' ? matrixValues[12] : matrixValues[4]);
}
